import type {
  CalendarItem,
  FeedItem,
  MasterLive,
  MasterVideo,
  PublicUserLiveItem,
  RoomResponse,
  UnifiedEvent,
  UpcomingLiveItem,
} from '../types/auth';
import { resolveName } from './displayName';
import { feedRefFor } from './feedRefId';

/**
 * Group D — adapter that maps the master's own content (rooms, lives,
 * uploaded videos) into the FeedItem shape so the same FeedCard +
 * FeedList components can render them uniformly.
 *
 * Subscription content (other masters) goes through api.getFeed(). Own
 * content has no aggregated endpoint yet, so we glue the three sources
 * here on the client.
 */

const PRACTICE_CATEGORIES = new Set(['practice', 'meditation', 'breathwork']);

function masterLiveToFeedItem(
  live: MasterLive,
  currentUserId: string,
  currentUserName: string,
  currentUserAvatar: string | null,
  currentMasterProfileId: number | null,
): FeedItem {
  const start = new Date(live.startDateTimeUtc);
  const end = start.getTime() + (live.durationMinutes ?? 60) * 60_000;
  const isPast = end <= Date.now() || !!live.playbackUrl;
  // Offline events are stored as MasterLive with type/Kind = 'Offline'.
  // The other BE strings ('Лайв-практика' / 'Лайв-стрим') map to FE
  // display "Live-практика" / "Live-сессия" per AMB-220 — but `type` here
  // is the raw BE field, so the lookup keys stay in legacy form.
  const isOffline = (live.type ?? '').toLowerCase().includes('offline') ||
    (live.type ?? '').toLowerCase().includes('офлайн');
  const kind: FeedItem['kind'] = isOffline
    ? (isPast ? 'offline_recorded' : 'offline_scheduled')
    : (isPast ? 'live_recorded' : 'live_scheduled');
  const loose = live as MasterLive & {
    address?: string | null;
    location?: string | null;
    tags?: string[] | string | null;
    tradition?: string | null;
  };
  const tags = Array.isArray(loose.tags)
    ? loose.tags
    : typeof loose.tags === 'string' && loose.tags.length > 0
      ? loose.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
      : [];
  return {
    kind,
    timestamp: live.startDateTimeUtc,
    refId: feedRefFor(kind, live.id),
    actorUserId: currentUserId,
    actorName: currentUserName,
    actorAvatarUrl: currentUserAvatar,
    title: live.title,
    coverImageUrl: live.coverImageUrl,
    startDateTimeUtc: live.startDateTimeUtc,
    durationMinutes: live.durationMinutes ?? null,
    savedMasterEventId: null,
    savedEventTitle: null,
    // Offline lives keep their directions in `tags`; online lives use the
    // `categories`/`category` fields. readEventDirections drops non-direction
    // tags, so passing the raw tag list for offline is safe.
    categories: isOffline && tags.length > 0
      ? tags
      : (live as { categories?: string[] | null }).categories ?? null,
    category: live.category ?? null,
    offlineEvent: isOffline
      ? {
          id: live.id,
          source: 'master',
          masterProfileId: currentMasterProfileId,
          organizerName: currentUserName,
          title: live.title,
          subtitle: null,
          description: live.description ?? null,
          coverImageUrl: live.coverImageUrl,
          eventType: 'meetup',
          startDateUtc: live.startDateTimeUtc,
          endDateUtc: new Date(end).toISOString(),
          location: loose.location ?? loose.address ?? null,
          address: loose.address ?? null,
          isOnline: false,
          price: null,
          spotsLeft: null,
          totalSpots: null,
          contactName: null,
          contactEmail: null,
          contactPhone: null,
          tags,
          tradition: loose.tradition ?? null,
          isRegistered: false,
          isSaved: false,
          createdAt: live.startDateTimeUtc,
        }
      : undefined,
  };
}

function roomToFeedItem(room: RoomResponse, currentUserId: string, currentUserName: string, currentUserAvatar: string | null): FeedItem {
  const start = room.scheduledAtUtc ? new Date(room.scheduledAtUtc) : null;
  const end = start ? start.getTime() + (room.durationMinutes ?? 60) * 60_000 : 0;
  const isPast = room.status === 'Ended' || !!room.endedAtUtc || (end > 0 && end <= Date.now());
  return {
    kind: isPast ? 'room_recorded' : 'room_scheduled',
    timestamp: room.scheduledAtUtc ?? room.createdAt,
    refId: feedRefFor(isPast ? 'room_recorded' : 'room_scheduled', room.id),
    actorUserId: currentUserId,
    actorName: currentUserName,
    actorAvatarUrl: currentUserAvatar,
    title: room.title ?? room.videoPracticeTitle,
    coverImageUrl: room.coverImageUrl ?? room.videoPracticeCoverImageUrl,
    startDateTimeUtc: room.scheduledAtUtc,
    durationMinutes: room.durationMinutes ?? null,
    savedMasterEventId: null,
    savedEventTitle: null,
    categories: room.categories ?? null,
    category: (room as { category?: string | null }).category ?? null,
    // CORE-1737 — обсуждение комнаты приходит с самой комнатой.
    discussionId: room.discussionId ?? null,
  };
}

function videoToFeedItem(video: MasterVideo, currentUserId: string, currentUserName: string, currentUserAvatar: string | null): FeedItem {
  return {
    kind: 'practice_uploaded',
    timestamp: video.publishedAtUtc ?? new Date().toISOString(),
    refId: feedRefFor('practice_uploaded', video.id),
    actorUserId: currentUserId,
    actorName: currentUserName,
    actorAvatarUrl: currentUserAvatar,
    title: video.title,
    coverImageUrl: video.coverImageUrl,
    startDateTimeUtc: null,
    durationMinutes: video.durationMinutes ?? null,
    savedMasterEventId: null,
    savedEventTitle: null,
    categories: (video as { categories?: string[] | null }).categories ?? null,
    category: video.category ?? null,
  };
}

export interface OwnPublicationsInput {
  rooms: RoomResponse[];
  lives: MasterLive[];
  videos: MasterVideo[];
  currentUserId: string;
  currentMasterProfileId?: number | null;
  currentUserName: string;
  currentUserAvatar: string | null;
}

/**
 * Combine the three master content sources into a single FeedItem array.
 * The result is unsorted — chip filters in StudioPublications apply their
 * own ordering.
 */
export function buildOwnPublications(input: OwnPublicationsInput): FeedItem[] {
  const { rooms, lives, videos, currentUserId, currentMasterProfileId = null, currentUserName, currentUserAvatar } = input;
  // AMB-263 follow-up — `getMyRooms` returns rooms where the user is
  // host OR participant OR subscriber, but the Studio surface is «Мои
  // публикации» (host-owned only). Without this gate every quick room
  // the master joined as a viewer would get tagged with
  // `actorUserId: currentUserId` → «ВАШЕ» badge + counted in the
  // «Показать ещё (33/70)» total. Veronika reported 70 visible vs 3
  // actually-uploaded items because of the joined-room leak.
  const ownedRooms = rooms.filter((r) => r.hostUserId === currentUserId);
  return [
    ...lives.map((l) => masterLiveToFeedItem(l, currentUserId, currentUserName, currentUserAvatar, currentMasterProfileId)),
    ...ownedRooms.map((r) => roomToFeedItem(r, currentUserId, currentUserName, currentUserAvatar)),
    ...videos.map((v) => videoToFeedItem(v, currentUserId, currentUserName, currentUserAvatar)),
  ];
}

// Mark unused PRACTICE_CATEGORIES as intentional for future use.
void PRACTICE_CATEGORIES;

/**
 * Convert an UpcomingLiveItem (api.getUpcomingLives — DashboardScreen's
 * canonical source for «events I follow / RSVP'd to») into a FeedItem.
 * This is the data path that actually has the user's subscriptions —
 * api.getFeed and api.getUserCalendar return empty for many accounts
 * because of how the backend partitions feed sources, but
 * getUpcomingLives is consistently populated.
 *
 * We respect the kind hint when available: an offline event is mapped
 * to offline_*, otherwise we default to live_* / room_* based on
 * structure. Past items become *_recorded.
 */
export function upcomingLiveToFeedItem(item: UpcomingLiveItem): FeedItem {
  const start = new Date(item.startDateTimeUtc).getTime();
  const end = start + (item.durationMinutes ?? 60) * 60_000;
  const isPast = end <= Date.now();
  // Heuristic: BE sometimes tags items with `eventType` / `isOffline` flags
  // that don't appear on the strict UpcomingLiveItem type — read them via
  // a loose cast so we don't fight the type system here.
  const loose = item as unknown as { eventType?: string; isOffline?: boolean; inviteCode?: string };
  const isOffline = !!loose.isOffline || (loose.eventType ?? '').toLowerCase() === 'offline';
  const isRoom = !!loose.inviteCode || (loose.eventType ?? '').toLowerCase() === 'room';
  const kind: FeedItem['kind'] = isOffline
    ? (isPast ? 'offline_recorded' : 'offline_scheduled')
    : isRoom
      ? (isPast ? 'room_recorded' : 'room_scheduled')
      : (isPast ? 'live_recorded' : 'live_scheduled');
  return {
    kind,
    timestamp: item.startDateTimeUtc,
    refId: feedRefFor(kind, item.id),
    actorUserId: `master-${item.master?.id ?? 0}`,
    actorName: resolveName(item.master?.name),
    actorAvatarUrl: item.master?.photoUrl ?? null,
    title: item.title,
    coverImageUrl: item.coverImageUrl,
    startDateTimeUtc: item.startDateTimeUtc,
    durationMinutes: item.durationMinutes,
    savedMasterEventId: null,
    savedEventTitle: null,
  };
}

/**
 * Convert a calendar subscription («Я иду» registration) into a FeedItem
 * so it can render in the consumer feed alongside subscription content.
 * Calendar items don't distinguish live/room/offline kinds — they all
 * surface as `live_*` for now (BE expansion is a follow-up).
 */
export function calendarItemToFeedItem(item: CalendarItem): FeedItem {
  const start = new Date(item.startDateTimeUtc).getTime();
  const end = start + (item.durationMinutes ?? 60) * 60_000;
  const isPast = end <= Date.now();
  return {
    kind: isPast ? 'live_recorded' : 'live_scheduled',
    timestamp: item.startDateTimeUtc,
    refId: feedRefFor(isPast ? 'live_recorded' : 'live_scheduled', item.id),
    actorUserId: `master-${item.masterProfileId}`,
    actorName: resolveName(item.masterName),
    actorAvatarUrl: null,
    title: item.title,
    coverImageUrl: item.coverImageUrl,
    startDateTimeUtc: item.startDateTimeUtc,
    durationMinutes: item.durationMinutes,
    savedMasterEventId: null,
    savedEventTitle: null,
  };
}

/**
 * Adapter for items returned by api.getPublicUserLives — both `hosting`
 * (events the master created) and `attending` (events the user RSVP'd to)
 * arrays use the same PublicUserLiveItem shape. We choose the FeedItem kind
 * based on PublicUserLiveItem.kind.code (LiveStream | Offline | LivePractice)
 * and whether the start has passed.
 *
 * `opts.isHost` is reserved for a future visual differentiation between
 * "ведёт" and "идёт" badges; today both branches produce the same payload.
 */
export function publicUserLiveToFeedItem(
  item: PublicUserLiveItem,
  opts: { isHost: boolean },
): FeedItem {
  void opts;
  const start = new Date(item.startDateTimeUtc).getTime();
  const end = start + (item.durationMinutes ?? 60) * 60_000;
  const isPast = end <= Date.now();

  // Heuristic kind detection. Primary signal is item.kind?.code from BE.
  // Fallback: WatchParty rooms come down with `inviteCode` / `eventType`
  // properties even when kind isn't set, so detect those before defaulting
  // to LiveStream — without this, a guest viewing a master's profile saw
  // hosted live-rooms badged as «ЛАЙВ-СЕССИЯ» (rev 4.7 fix).
  const loose = item as unknown as { eventType?: string; inviteCode?: string; isOffline?: boolean };
  const code = item.kind?.code ?? (
    loose.isOffline ? 'Offline'
    : (loose.inviteCode || (loose.eventType ?? '').toLowerCase() === 'room' || (loose.eventType ?? '').toLowerCase() === 'livepractice')
      ? 'LivePractice'
      : 'LiveStream'
  );
  let kind: FeedItem['kind'];
  if (code === 'Offline') {
    kind = isPast ? 'offline_recorded' : 'offline_scheduled';
  } else if (code === 'LivePractice') {
    kind = isPast ? 'room_recorded' : 'room_scheduled';
  } else {
    kind = isPast ? 'live_recorded' : 'live_scheduled';
  }

  return {
    kind,
    timestamp: item.startDateTimeUtc,
    refId: feedRefFor(kind, item.id),
    actorUserId: `master-${item.master?.id ?? 0}`,
    actorName: resolveName(item.master?.name),
    actorAvatarUrl: item.master?.photoUrl ?? null,
    title: item.title,
    coverImageUrl: item.coverImageUrl,
    coverVariants: item.coverVariants ?? null,
    startDateTimeUtc: item.startDateTimeUtc,
    durationMinutes: item.durationMinutes,
    savedMasterEventId: null,
    savedEventTitle: null,
    participantsCount: item.participantsCount ?? null,
    categories: item.categories ?? null,
    category: item.category ?? null,
  };
}

/**
 * Adapter for `UnifiedEvent` (calendar / Agapella-curated events surfaced
 * via EventCard on CircleChat / AgapeCenter screens). We normalise into
 * FeedItem so the same SavedEventsContext snapshot path serves these
 * surfaces — `event_saved` kind for everything except practices, which
 * use `practice_uploaded` (→ «Практики» tab). Both kinds map to
 * the BE `master` source via `feedKindToSource`.
 */
export function unifiedEventToFeedItem(ev: UnifiedEvent): FeedItem {
  const kind: FeedItem['kind'] = ev.eventType === 'practice'
    ? 'practice_uploaded'
    : 'event_saved';
  return {
    kind,
    refId: feedRefFor(kind, ev.id),
    actorUserId: ev.masterProfileId != null ? `master-${ev.masterProfileId}` : `agape-${ev.id}`,
    actorName: ev.organizerName,
    actorAvatarUrl: null,
    title: ev.title,
    coverImageUrl: ev.coverImageUrl,
    startDateTimeUtc: ev.startDateUtc,
    durationMinutes: null,
    timestamp: ev.startDateUtc ?? ev.createdAt,
    savedMasterEventId: ev.id,
    savedEventTitle: ev.title,
    isSaved: true,
  };
}

/**
 * CORE-226 — public-discovery gate for unified events. `/api/events` returns a
 * master their OWN private events (CORE-89) so the management «События» tab can
 * list them, but public-discovery surfaces (platform calendar, home hero) must
 * exclude them. Public + platform (agape) events pass; anonymous callers never
 * receive privates anyway. Undefined `isPrivate` (older payloads / platform
 * events) is treated as public.
 */
export function isPublicDiscoverableEvent(ev: Pick<UnifiedEvent, 'isPrivate'>): boolean {
  return ev.isPrivate !== true;
}

/**
 * Adapter for offline items from `/api/events`. The studio already receives
 * own MasterLive rows from `/api/master/content/lives`; this fills the gap
 * for offline events exposed only through the unified events calendar feed.
 */
export function offlineUnifiedEventToFeedItem(
  ev: UnifiedEvent,
  actor?: { userId?: string | null; name?: string | null; avatarUrl?: string | null },
): FeedItem {
  const start = new Date(ev.startDateUtc).getTime();
  const end = ev.endDateUtc
    ? new Date(ev.endDateUtc).getTime()
    : start + 60 * 60_000;
  const isPast = Number.isFinite(end) && end <= Date.now();
  const actorUserId = actor?.userId ?? (ev.masterProfileId != null ? `master-${ev.masterProfileId}` : `agape-${ev.id}`);

  return {
    kind: isPast ? 'offline_recorded' : 'offline_scheduled',
    refId: feedRefFor(isPast ? 'offline_recorded' : 'offline_scheduled', ev.id),
    actorUserId,
    actorName: resolveName(actor?.name ?? ev.organizerName),
    actorAvatarUrl: actor?.avatarUrl ?? null,
    title: ev.title,
    coverImageUrl: ev.coverImageUrl,
    coverVariants: ev.coverVariants ?? null,
    startDateTimeUtc: ev.startDateUtc,
    durationMinutes: ev.endDateUtc && Number.isFinite(start)
      ? Math.max(0, Math.round((new Date(ev.endDateUtc).getTime() - start) / 60_000))
      : null,
    timestamp: ev.startDateUtc ?? ev.createdAt,
    savedMasterEventId: null,
    savedEventTitle: null,
    // Offline events store their directions in `tags` (the unified-events
    // catalogue has no `categories` field). readEventDirections normalises
    // these and silently drops any non-direction tags.
    categories: ev.tags && ev.tags.length > 0
      ? ev.tags
      : (ev as { categories?: string[] | null }).categories ?? null,
    category: (ev as { category?: string | null }).category ?? null,
    offlineEvent: ev,
  };
}

/**
 * Merge two FeedItem arrays and dedupe by `${kind}-${refId}`. Used to
 * combine api.getFeed() (subscription content) with api.getUserCalendar()
 * (events the user RSVP'd to) — they may overlap.
 */
export function mergeFeedItems(...sources: FeedItem[][]): FeedItem[] {
  const seen = new Set<string>();
  const result: FeedItem[] = [];
  for (const arr of sources) {
    for (const item of arr) {
      const key = `${item.kind}-${item.refId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}
